import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  companySearchAsync,
  selectCompanyAsync,
} from "../../redux/features/company/companyThunks";
import { clearCompanyDataExceptList } from "../../redux/features/company/companySlice";
import { clearAllCateringData } from "../../redux/features/restaurants/restaurantSlice";
import {
  selectCompanyList,
  selectSearchedCompany,
  selectCompanySelectedAddress,
} from "../../redux/features/company/companySelectors";
import { selectServiceType } from "../../redux/features/customer/customerSelectors";
import { setCompanySelectedAddress } from "../../redux/features/company/companySlice";
import NewCompanyForm from "../NewCompanyForm";
import AddressBlock from "../addAddress/addressBlock";
import AddressForm from "../addAddress/addressForm";
import PastOrders from "../pastOrders";
import CateringPastOrders from "../pastOrders/CateringPastOrders";
import { transformAddresses } from "../../utils/addresses";
import EditCompanyIcon from "../../assets/icons/EditCompanyIcon";
import { AvailableBrands } from "../../components/availableBrands";
import { selectCateringBrandsList, selectPastOrderAddressId } from "../../redux/features/restaurants/restaurantSelectors";
import { getAllCateringRestaurantsAsync } from "../../redux/features/restaurants/restaurantThunks";
import { clearPastOrderAddressId } from "../../redux/features/restaurants/restaurantSlice";

export const CorporateCatering = ({ isCheckout = false, setShowAddressForm, isReadonly = false }) => {
  const dispatch = useDispatch();
  
  // Get company data from Redux state
  const companyList = useSelector(selectCompanyList);
  const searchedCompany = useSelector(selectSearchedCompany);
  const serviceType = useSelector(selectServiceType);
  const branchesData = useSelector(selectCateringBrandsList);
  const companySelectedAddress = useSelector(selectCompanySelectedAddress);
  const pastOrderAddressId = useSelector(selectPastOrderAddressId);

  // Company search state
  const [companySearch, setCompanySearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [companyLoadAttempted, setCompanyLoadAttempted] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [showNewCompanyForm, setShowNewCompanyForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  
  // Filter companies from Redux state based on search term
  const companiesArray = Array.isArray(companyList) ? companyList : [];
  const filteredCompanies = companySearch 
    ? companiesArray.filter((company) =>
        company.name && company.name.toLowerCase().includes(companySearch.toLowerCase())
      )
    : companiesArray;

  // Effect to handle company search API calls
  useEffect(() => {
    if (serviceType === "corporate catering") {
      const companiesArray = Array.isArray(companyList) ? companyList : [];
      
      if (companiesArray.length === 0 && !companyLoadAttempted && !companyLoading) {
        setCompanyLoading(true);
        setCompanyLoadAttempted(true);
        dispatch(companySearchAsync())
          .then(() => {
            setCompanyLoading(false);
          })
          .catch(() => {
            setCompanyLoading(false);
          });
      }
    }
  }, [serviceType, companyList, dispatch, companyLoadAttempted, companyLoading]);

  // Reset company load attempt when service type changes to corporate catering
  useEffect(() => {
    if (serviceType === "corporate catering") {
      setCompanyLoadAttempted(false);
    }
  }, [serviceType]);

  // Ensure catering brands are loaded when component mounts and address is selected
  useEffect(() => {
    if (serviceType === "corporate catering" && companySelectedAddress && (!branchesData || branchesData.length === 0)) {
      dispatch(getAllCateringRestaurantsAsync());
    }
  }, [serviceType, companySelectedAddress, branchesData, dispatch]);

  const companyAddresses = searchedCompany && Array.isArray(searchedCompany.addresses) ? searchedCompany.addresses : [];
  const transformedAddresses = transformAddresses(companyAddresses);

  const handleSelectCompanyAddress = (id) => {
    // Find the selected address object from the transformed addresses
    const selectedAddress = transformedAddresses.find(address => address.id === id);
    dispatch(setCompanySelectedAddress(selectedAddress));
    
    // Ensure catering brands are loaded when an address is selected
    if (selectedAddress && (!branchesData || branchesData.length === 0)) {
      dispatch(getAllCateringRestaurantsAsync());
    }
  };

  // Check for past order address restoration for corporate catering - from Redux
  useEffect(() => {
    if (pastOrderAddressId && searchedCompany && searchedCompany.addresses && !companySelectedAddress) {
      console.log('Looking for company address ID from past order (Redux):', pastOrderAddressId, 'in company addresses:', searchedCompany.addresses);
      
      // Find the matching address from company's addresses
      const matchingAddress = searchedCompany.addresses.find(addr => addr.id === pastOrderAddressId);
      
      if (matchingAddress) {
        // Use the same transformation logic as the component already uses
        const transformedAddresses = transformAddresses([matchingAddress]);
        const transformedAddress = transformedAddresses[0];
        
        console.log('Found matching company address from past order:', transformedAddress);
        dispatch(setCompanySelectedAddress(transformedAddress));
        
        // Clear the Redux flag after successful matching
        dispatch(clearPastOrderAddressId());
      } else {
        console.warn('Could not find matching company address for past order address ID:', pastOrderAddressId);
      }
    }
  }, [searchedCompany, companySelectedAddress, pastOrderAddressId, dispatch]);

  const renderCompanyAddressesAndOrders = () => {
    if (searchedCompany && companyAddresses.length > 0) {
      return (
        <div className="w-full flex gap-4 mt-4">
          {/* Left side: Address List and Past Orders */}
          <div className="flex-grow flex-shrink-0 min-w-0 space-y-4">
            {/* Address List */}
            <div>
              <h1 className="text-3xl font-bold text-gray-500 mb-4">Address List</h1>
              <div className="w-full overflow-y-auto overflow-x-hidden max-h-[550px] pr-2">
                <div
                  className="grid gap-6"
                  style={{ gridTemplateColumns: "repeat(auto-fill, minmax(257px, 1fr))" }}
                >
                  {transformedAddresses.map((address, index) => (
                    <AddressBlock
                      key={address.id || index}
                      address={address}
                      isAddressBlockVisible
                      isSelected={companySelectedAddress?.id === address.id}
                      onSelect={handleSelectCompanyAddress}
                      selectedAddress={companySelectedAddress || null}
                      isCheckout={isCheckout}
                      showSwitchButton={false}
                      setShowAddressForm={setShowAddressForm}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Catering Past Orders - below address list */}
            <div>
              <CateringPastOrders />
            </div>
          </div>

          {/* Right side: Available Brands - only show when address is selected and not readonly */}
          {branchesData && branchesData.length > 0 && companySelectedAddress !== null && !isReadonly && (
            <div className="flex-grow flex-shrink-0 min-w-0">
              <AvailableBrands 
                branchesData={branchesData} 
              />
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-row gap-8">
      {/* Left side: Company Search */}
      <div className="flex flex-col">
        {/* Company Search and Add Button Row */}
        <div className="flex flex-row gap-4 items-start mb-4">
          <div className="relative" style={{ minWidth: '372px' }}>
            <input
              type="text"
              placeholder="Search Company"
              value={searchedCompany ? searchedCompany.name : companySearch}
              onChange={(e) => {
                if (!isReadonly) {
                  setCompanySearch(e.target.value);
                  setShowDropdown(true);
                  if (searchedCompany && e.target.value !== searchedCompany.name) {
                    dispatch(clearAllCateringData()); // Clear catering data when changing search
                    dispatch(selectCompanyAsync(null));
                  }
                }
              }}
              onFocus={() => !isReadonly && setShowDropdown(true)}
              onBlur={() => !isReadonly && setTimeout(() => setShowDropdown(false), 250)}
              readOnly={isReadonly}
              className={`border border-gray-300 bg-neutral-50 rounded-xl px-4 py-2 w-full h-12 focus:ring-1 focus:ring-gray-300 focus:outline-none placeholder-gray-400 text-gray-500 font-bold ${isReadonly ? 'cursor-not-allowed bg-gray-100' : ''}`}
              style={{ minWidth: '372px', paddingRight: (companySearch || searchedCompany) ? '72px' : '40px', height: '48px' }}
            />
            {(companySearch || searchedCompany) && !isReadonly && (
              <button
                type="button"
                className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                 onClick={() => {
                   setCompanySearch("");
                   setShowAddressForm(false);
                   dispatch(clearCompanyDataExceptList());
                   dispatch(clearAllCateringData()); // Clear all catering data
                   dispatch(setCompanySelectedAddress(null)); // Clear selected company address
                   setShowNewCompanyForm(false);
                   setEditingCompany(null);
                 }}
                tabIndex={-1}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            )}
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
              </svg>
            </span>
            
            {showDropdown && !isReadonly && (
              <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-b-xl shadow-lg max-h-60 overflow-y-auto">
                {filteredCompanies.length > 0 && filteredCompanies.map((company) => (
                  <div
                    key={company.id || company.name}
                    className="px-4 py-2 text-gray-500 cursor-pointer hover:bg-gray-100 flex justify-between items-center"
                    onMouseDown={() => {
                      setCompanySearch(company.name);
                      setShowDropdown(false);
                      dispatch(clearAllCateringData()); // Clear catering data when selecting new company
                      dispatch(selectCompanyAsync(company));
                      setShowNewCompanyForm(false);
                    }}
                  >
                    <span>{company.name}</span>
                    <div
                      className="bg-gray-300 rounded-full p-1 hover:bg-gray-400"
                      onMouseDown={(e) => {
                        e.stopPropagation(); // Prevent selecting the company
                        setEditingCompany(company);
                        setShowNewCompanyForm(true);
                        setShowDropdown(false);
                      }}
                    >
                      <EditCompanyIcon className="w-4 h-4" />
                    </div>
                  </div>
                ))}
                
                {(!filteredCompanies || filteredCompanies.length === 0) && !companyLoading && (
                   <div className="px-4 py-2 text-gray-400">
                      {companyLoadAttempted && companiesArray.length === 0 ? "Failed to load" : "No companies found"}
                   </div>
                )}
                
                {companyLoading && <div className="px-4 py-2 text-gray-400">Loading...</div>}

                {/* Add New Company Button */}
                {!searchedCompany && (
                  <div className="border-t border-gray-200 p-2">
                     <button
                        type="button"
                        onClick={() => {
                          setEditingCompany(null); // Reset editing company
                          setShowNewCompanyForm(true);
                          setShowDropdown(false);
                        }}
                        className="text-white font-bold bg-amber-800 rounded-xl h-12 w-full focus:ring-1 focus:ring-amber-800 focus:outline-none hover:bg-amber-900"
                      >
                        Add New Company
                     </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Add Address Button only when a company is selected and not readonly */}
          {searchedCompany && !isReadonly && (
            <div className="flex items-center h-12">
                             <button
                 type="button"
                 onClick={() => setShowAddressForm(true)}
                 className="text-white font-bold bg-amber-800 rounded-xl h-12 w-full px-4 focus:ring-1 focus:ring-amber-800 focus:outline-none hover:bg-amber-900"
               >
                 Add New Address
               </button>
            </div>
          )}
        </div>
        
        {/* Display selected address in checkout */}
        {isCheckout && companySelectedAddress && (
          <div className="mt-4">
            <h2 className="text-lg font-bold text-gray-500 mb-2">Catering Address</h2>
            <div className="min-w-[257px] max-w-[257px] w-[257px] h-[162px] border-2 border-amber-100 bg-neutral-50 p-4 rounded-xl flex flex-col justify-between">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-lg text-gray-500 truncate overflow-hidden">{companySelectedAddress.firstLine}</h3>
                {companySelectedAddress.active_order === "true" && (
                  <button className="w-20 h-5 bg-emerald-600 text-white border border-white rounded-lg font-bold text-xs flex-shrink-0 ml-1">
                    Active Order
                  </button>
                )}
              </div>
              <h4 className="text-gray-500 text-sm truncate">{companySelectedAddress.area}</h4>
              {companySelectedAddress.houseName && <h4 className="text-gray-500 text-sm truncate">{companySelectedAddress.houseName}</h4>}
              {companySelectedAddress.buildingNumber && <h5 className="text-gray-500 text-sm truncate">{companySelectedAddress.buildingNumber}</h5>}
              {companySelectedAddress.floor && <h6 className="text-gray-500 text-sm truncate">{companySelectedAddress.floor}</h6>}
              {companySelectedAddress.number && <p className="text-gray-500 text-sm truncate">{companySelectedAddress.number}</p>}
            </div>
          </div>
        )}
        
        {/* Render company addresses only when a company is selected and not in checkout */}
        {searchedCompany && !isCheckout && renderCompanyAddressesAndOrders()}
      </div>

      {/* Right side: New Company Form */}
      {showNewCompanyForm && (
        <div className="w-full max-w-md">
          <NewCompanyForm
            editingCompany={editingCompany}
            onSave={({ isEdit }) => {
              setShowNewCompanyForm(false);
              setEditingCompany(null);
              setCompanyLoadAttempted(false);
              dispatch(companySearchAsync());
              
              // If editing, clear the search and company selection to force refresh
              if (isEdit) {
                setCompanySearch("");
                dispatch(clearCompanyDataExceptList());
              }
            }}
            onCancel={() => {
              setShowNewCompanyForm(false);
              setEditingCompany(null);
            }}
          />
        </div>
      )}
      
    </div>
  );
}; 